// src/utils/blackScholes.js
// Black-Scholes option pricing engine for frontend P&L matrix computation.
// Matches the Python implementation in MarketDataIngestionController.py (lines 990-1011).

const RISK_FREE_RATE = 0.05;

/**
 * Safely parse a date string, stripping any time component before appending
 * 'T12:00:00' to avoid timezone issues and invalid-date bugs.
 * Handles: "2026-03-28", "2026-03-28T00:00:00", "2026-03-28T00:00:00.000Z"
 */
function safeParseDate(dateStr) {
    if (!dateStr) return new Date(NaN);
    const datePart = String(dateStr).substring(0, 10);
    return new Date(datePart + 'T12:00:00');
}

/**
 * Standard Normal CDF — Abramowitz & Stegun approximation (7-decimal accuracy).
 */
function normalCDF(x) {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const sign = x < 0 ? -1 : 1;
    const absX = Math.abs(x) / Math.SQRT2;
    const t = 1.0 / (1.0 + p * absX);
    const y =
        1.0 -
        ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-absX * absX);
    return 0.5 * (1.0 + sign * y);
}

/**
 * Standard Normal PDF.
 */
function normalPDF(x) {
    return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

/**
 * Black-Scholes option theoretical price.
 * @param {number} S - underlying price
 * @param {number} K - strike price
 * @param {number} T - time to expiry in years (must be > 0)
 * @param {number} r - risk-free rate
 * @param {number} sigma - implied volatility (decimal, e.g. 0.30)
 * @param {string} optionType - 'CALL' or 'PUT'
 * @returns {number} theoretical option price
 */
export function blackScholesPrice(S, K, T, r, sigma, optionType) {
    // At or past expiration — return intrinsic value
    if (T <= 0) {
        if (optionType === 'CALL') return Math.max(S - K, 0);
        return Math.max(K - S, 0);
    }

    if (K <= 0 || sigma <= 0) return 0;

    const sqrtT = Math.sqrt(T);
    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
    const d2 = d1 - sigma * sqrtT;

    if (optionType === 'CALL') {
        return S * normalCDF(d1) - K * Math.exp(-r * T) * normalCDF(d2);
    }
    return K * Math.exp(-r * T) * normalCDF(-d2) - S * normalCDF(-d1);
}

/**
 * Calculate Greeks for a single option.
 * @returns {{ delta: number, gamma: number, theta: number, vega: number, rho: number }}
 */
export function calculateGreeks(S, K, T, r, sigma, optionType) {
    if (T <= 0 || K <= 0 || sigma <= 0) {
        const intrinsic =
            optionType === 'CALL' ? Math.max(S - K, 0) : Math.max(K - S, 0);
        return {
            delta: intrinsic > 0 ? (optionType === 'CALL' ? 1 : -1) : 0,
            gamma: 0,
            theta: 0,
            vega: 0,
            rho: 0,
        };
    }

    const sqrtT = Math.sqrt(T);
    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
    const d2 = d1 - sigma * sqrtT;
    const nd1 = normalPDF(d1);
    const expRT = Math.exp(-r * T);

    let delta, theta, rho;

    if (optionType === 'CALL') {
        delta = normalCDF(d1);
        theta =
            (-(S * nd1 * sigma) / (2 * sqrtT) - r * K * expRT * normalCDF(d2)) / 365;
        rho = (K * T * expRT * normalCDF(d2)) / 100;
    } else {
        delta = normalCDF(d1) - 1;
        theta =
            (-(S * nd1 * sigma) / (2 * sqrtT) + r * K * expRT * normalCDF(-d2)) / 365;
        rho = (-K * T * expRT * normalCDF(-d2)) / 100;
    }

    const gamma = nd1 / (S * sigma * sqrtT);
    const vega = (S * nd1 * sqrtT) / 100;

    return {
        delta: parseFloat(delta.toFixed(4)),
        gamma: parseFloat(gamma.toFixed(6)),
        theta: parseFloat(theta.toFixed(4)),
        vega: parseFloat(vega.toFixed(4)),
        rho: parseFloat(rho.toFixed(4)),
    };
}

/**
 * Calculate aggregate net Greeks for a multi-leg position.
 * Sign convention: BUY = +1, SELL = -1 (matches OptionsContractManagementController.py:1248-1261).
 */
export function calculateNetGreeks(legs, underlyingPrice, riskFreeRate = RISK_FREE_RATE, ivMultiplier = 1.0) {
    let netDelta = 0, netGamma = 0, netTheta = 0, netVega = 0, netRho = 0;

    for (const leg of legs) {
        const sign = leg.side === 'BUY' ? 1 : -1;
        const qty = leg.quantity || 1;
        const sigma = (leg.implied_volatility || 0.3) * ivMultiplier;
        const T = Math.max((leg.dte || 0) / 365, 0.001);

        const greeks = calculateGreeks(
            underlyingPrice,
            leg.strike_price,
            T,
            riskFreeRate,
            sigma,
            leg.option_type
        );

        netDelta += sign * qty * greeks.delta * 100;
        netGamma += sign * qty * greeks.gamma * 100;
        netTheta += sign * qty * greeks.theta * 100;
        netVega += sign * qty * greeks.vega * 100;
        netRho += sign * qty * greeks.rho * 100;
    }

    return {
        delta: parseFloat(netDelta.toFixed(2)),
        gamma: parseFloat(netGamma.toFixed(2)),
        theta: parseFloat(netTheta.toFixed(2)),
        vega: parseFloat(netVega.toFixed(2)),
        rho: parseFloat(netRho.toFixed(2)),
    };
}

/**
 * Compute the 3rd Friday of a given month/year (standard options expiration).
 */
function getThirdFriday(year, month) {
    // First day of the month
    const first = new Date(year, month, 1);
    // Day of week: 0=Sun, 5=Fri
    const firstFriday = 1 + ((5 - first.getDay() + 7) % 7);
    // 3rd Friday = first Friday + 14 days
    return new Date(year, month, firstFriday + 14, 12, 0, 0, 0);
}

/**
 * Build adaptive date axis matching optionstrat.com:
 *  - Days 0-30: every trading day (weekdays)
 *  - Days 31-90: weekly (Fridays)
 *  - Days 91+: monthly 3rd-Friday expiration dates
 * Always includes the expiration date as the last column.
 */
function buildAdaptiveDates(startDate, endDate) {
    const dates = [];
    const seen = new Set();

    const formatDate = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const addDate = (d) => {
        const key = formatDate(d);
        if (!seen.has(key)) {
            seen.add(key);
            dates.push(key);
        }
    };

    // Phase 1: Daily (weekdays) for days 0-30
    const phase1End = new Date(startDate);
    phase1End.setDate(phase1End.getDate() + 30);
    const dailyLimit = phase1End < endDate ? phase1End : endDate;

    for (let d = new Date(startDate); d <= dailyLimit; d.setDate(d.getDate() + 1)) {
        if (d.getDay() !== 0 && d.getDay() !== 6) {
            addDate(new Date(d));
        }
    }

    // Phase 2: Weekly (Fridays) for days 31-90
    if (endDate > phase1End) {
        const phase2End = new Date(startDate);
        phase2End.setDate(phase2End.getDate() + 90);
        const weeklyLimit = phase2End < endDate ? phase2End : endDate;

        // Find the first Friday after phase1End
        let d = new Date(phase1End);
        while (d.getDay() !== 5) d.setDate(d.getDate() + 1);

        for (; d <= weeklyLimit; d.setDate(d.getDate() + 7)) {
            addDate(new Date(d));
        }

        // Phase 3: Monthly 3rd Fridays for days 91+
        if (endDate > phase2End) {
            let m = new Date(phase2End);
            m.setDate(1);
            m.setMonth(m.getMonth() + 1); // Start from next full month

            while (m <= endDate) {
                const thirdFri = getThirdFriday(m.getFullYear(), m.getMonth());
                if (thirdFri > phase2End && thirdFri <= endDate) {
                    addDate(thirdFri);
                }
                m.setMonth(m.getMonth() + 1);
            }
        }
    }

    // Always include the expiration date itself (nearest weekday if weekend)
    let expDateAdjusted = new Date(endDate);
    if (expDateAdjusted.getDay() === 0) expDateAdjusted.setDate(expDateAdjusted.getDate() - 2);
    if (expDateAdjusted.getDay() === 6) expDateAdjusted.setDate(expDateAdjusted.getDate() - 1);
    addDate(expDateAdjusted);

    // Sort chronologically
    dates.sort();
    return dates;
}

/**
 * Generate a P&L matrix across a range of underlying prices and dates.
 *
 * Uses adaptive date sampling (daily near-term, weekly mid-term, monthly far-term)
 * matching optionstrat.com layout.
 *
 * @param {Object} params
 * @param {Array} params.legs - Array of { side, option_type, strike_price, expiration_date, entry_price, quantity, implied_volatility }
 * @param {number} params.underlyingPrice - Current underlying price
 * @param {{ min: number, max: number }} params.priceRange - Percentage range (e.g. { min: -23, max: 23 })
 * @param {number} params.priceSteps - Number of price rows (default 22)
 * @param {{ start: Date, end: Date }} params.dateRange - Date range for columns
 * @param {number} params.riskFreeRate - Risk-free rate (default 0.05)
 * @param {number} params.ivMultiplier - IV multiplier (default 1.0)
 * @returns {{ prices: number[], dates: string[], matrix: number[][], maxProfit: number, maxLoss: number, netGreeks: Object }}
 */
export function generatePnLMatrix({
    legs,
    underlyingPrice,
    priceRange = { min: -23, max: 23 },
    priceSteps = 22,
    dateRange,
    riskFreeRate = RISK_FREE_RATE,
    ivMultiplier = 1.0,
}) {
    // Build price axis (high to low, like optionstrat)
    const minPrice = underlyingPrice * (1 + priceRange.min / 100);
    const maxPrice = underlyingPrice * (1 + priceRange.max / 100);
    const priceStep = (maxPrice - minPrice) / (priceSteps - 1);
    const prices = [];
    for (let i = priceSteps - 1; i >= 0; i--) {
        prices.push(parseFloat((minPrice + priceStep * i).toFixed(2)));
    }

    // Build date axis with adaptive sampling
    const startDate = new Date(dateRange.start);
    let endDate = new Date(dateRange.end);
    startDate.setHours(12, 0, 0, 0);
    endDate.setHours(12, 0, 0, 0);

    // Ensure at least 30 calendar days
    const minEndDate = new Date(startDate);
    minEndDate.setDate(minEndDate.getDate() + 30);
    if (endDate < minEndDate) endDate = minEndDate;

    const dates = buildAdaptiveDates(startDate, endDate);

    // Compute P&L matrix
    // P&L = sum of each leg's (theoreticalPrice - entryPrice) * sign * qty * 100
    let maxProfit = -Infinity;
    let maxLoss = Infinity;

    const matrix = prices.map((price) => {
        return dates.map((dateStr) => {
            const evalDate = safeParseDate(dateStr);
            let totalPnl = 0;

            for (const leg of legs) {
                const sign = leg.side === 'BUY' ? 1 : -1;
                const qty = leg.quantity || 1;
                const sigma = (leg.implied_volatility || 0.3) * ivMultiplier;
                const expDate = safeParseDate(leg.expiration_date);

                // Time to expiration from eval date (in years)
                const daysToExp = Math.max(
                    (expDate.getTime() - evalDate.getTime()) / (1000 * 60 * 60 * 24),
                    0
                );
                const T = daysToExp / 365;

                const theoreticalPrice = blackScholesPrice(
                    price,
                    leg.strike_price,
                    T,
                    riskFreeRate,
                    sigma,
                    leg.option_type
                );

                const legPnl = sign * qty * (theoreticalPrice - leg.entry_price) * 100;
                totalPnl += legPnl;
            }

            totalPnl = parseFloat(totalPnl.toFixed(2));
            if (totalPnl > maxProfit) maxProfit = totalPnl;
            if (totalPnl < maxLoss) maxLoss = totalPnl;

            return totalPnl;
        });
    });

    // Calculate net Greeks at current price with consistent date parsing
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const legsWithDte = legs.map((leg) => ({
        ...leg,
        dte: Math.max(
            (safeParseDate(leg.expiration_date).getTime() - today.getTime()) /
                (1000 * 60 * 60 * 24),
            0
        ),
    }));
    const netGreeks = calculateNetGreeks(legsWithDte, underlyingPrice, riskFreeRate, ivMultiplier);

    return {
        prices,
        dates,
        matrix,
        maxProfit: maxProfit === -Infinity ? 0 : maxProfit,
        maxLoss: maxLoss === Infinity ? 0 : maxLoss,
        netGreeks,
    };
}

/**
 * Transform raw P&L matrix to % of Max Risk.
 * Cell = (pnl / |maxLoss|) × 100
 */
export function transformToPercentOfMaxRisk(matrix, maxLoss) {
    const absMaxLoss = Math.abs(maxLoss) || 1;
    return matrix.map((row) =>
        row.map((val) => parseFloat(((val / absMaxLoss) * 100).toFixed(1)))
    );
}

/**
 * Transform raw P&L matrix to P&L percentage.
 * Cell = (pnl / |netCostBasis|) × 100
 */
export function transformToProfitLossPercent(matrix, netCostBasis) {
    const basis = Math.abs(netCostBasis) || 1;
    return matrix.map((row) =>
        row.map((val) => parseFloat(((val / basis) * 100).toFixed(1)))
    );
}

/**
 * Transform raw P&L matrix to contract value.
 * Cell = entryValue + pnl
 */
export function transformToContractValue(matrix, entryValue) {
    return matrix.map((row) =>
        row.map((val) => parseFloat((entryValue + val).toFixed(2)))
    );
}
